import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { getMergeQueuesUrl } from 'src/components/merge-queues/constants';
import { useFlag } from 'src/hooks/flag';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../merge-queues.i18n';
import { UseMergeQueueActionParams } from '../types';

const clearMergeQueueRequest = async ({
  mergeQueueUuid,
  owner,
  slug,
}: Omit<UseMergeQueueActionParams, 'onClose' | 'onSuccess'>) => {
  const url = `${getMergeQueuesUrl(owner, slug)}/${mergeQueueUuid}/clear`;

  const response = await fetch(
    authRequest(url, {
      method: 'POST',
      headers: jsonHeaders,
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to clear merge queue: ${response.status} ${response.statusText}`
    );
  }

  return Promise.resolve();
};

export const useClearMergeQueue = ({
  mergeQueueUuid,
  owner,
  slug,
  onClose,
}: Omit<UseMergeQueueActionParams, 'onSuccess'>) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();

  const clearMergeQueueCallback = useCallback(
    () => clearMergeQueueRequest({ mergeQueueUuid, owner, slug }),
    [mergeQueueUuid, owner, slug]
  );

  const { mutate: clearMergeQueue, isPending: isClearingMergeQueue } =
    useMutation(clearMergeQueueCallback, {
      onSuccess: () => {
        showSuccessFlag(formatMessage(messages.mergeQueueClearSuccess));
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'merge-queue-configuration',
          action: 'clear-merge-queue',
        });
        showErrorFlag(formatMessage(messages.mergeQueueClearError));
      },
      onFinished: () => {
        onClose();
      },
    });

  return {
    clearMergeQueue,
    isClearingMergeQueue,
  };
};
